import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { getOperationalHours, updateOperationalHours } from '../services/branchService';

const FULL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const OperationalHoursEditor = ({ branchId, onSaved }) => {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);
  const [pickerVisible, setPickerVisible] = useState(false);
  const [pickerState, setPickerState] = useState({ dayIndex: 0, periodIndex: 0, breakIndex: -1, field: 'openingTime' });
  const [tempHour, setTempHour] = useState('09');
  const [tempMinute, setTempMinute] = useState('00');
  const [tempAmPm, setTempAmPm] = useState('AM');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!branchId) return;
    try {
      setLoading(true);
      const existing = await getOperationalHours(branchId);
      if (Array.isArray(existing) && existing.length > 0) {
        const byDay = new Map(existing.map((h) => [h.dayOfWeek || h.day, h]));
        const mapped = FULL_DAYS.map(day => {
          const item = byDay.get(day) || {};
          const periods = (item.periods || []).map(p => ({
            openingTime: p.openingTime || '09:00',
            closingTime: p.closingTime || '22:00',
            breakTimes: (p.breakTimes || []).map(b => ({ startTime: b.startTime || '12:00', endTime: b.endTime || '13:00' })),
          }));
          return { dayOfWeek: day, isOpen: item.isOpen !== false, periods: periods.length ? periods : [{ openingTime: '09:00', closingTime: '22:00', breakTimes: [] }] };
        });
        setDays(mapped);
      } else {
        setDays(FULL_DAYS.map(d => ({ dayOfWeek: d, isOpen: true, periods: [{ openingTime: '09:00', closingTime: '22:00', breakTimes: [] }] })));
      }
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const formatTimeForDisplay = (time24) => {
    if (!time24) return 'Set Time';
    const [h, m] = String(time24).split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const from24h = (time24) => {
    const [h, m] = String(time24 || '09:00').split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return { hour: String(hour12).padStart(2, '0'), minute: String(m).padStart(2, '0'), ampm };
  };

  const to24h = (hourStr, minuteStr, ampm) => {
    let h = parseInt(hourStr, 10);
    const m = parseInt(minuteStr, 10);
    if (isNaN(h) || isNaN(m)) return '09:00';
    h = Math.max(1, Math.min(12, h));
    const mm = Math.max(0, Math.min(59, m));
    if (ampm === 'PM' && h !== 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  };

  const showPicker = (dayIndex, periodIndex, breakIndex, field) => {
    setPickerState({ dayIndex, periodIndex, breakIndex, field });
    const current = breakIndex >= 0 ? days[dayIndex].periods[periodIndex].breakTimes[breakIndex][field] : days[dayIndex].periods[periodIndex][field];
    const { hour, minute, ampm } = from24h(current);
    setTempHour(hour);
    setTempMinute(minute);
    setTempAmPm(ampm);
    setPickerVisible(true);
  };

  const confirmPicker = () => {
    const t24 = to24h(tempHour, tempMinute, tempAmPm);
    setDays(prev => {
      const next = [...prev];
      const d = { ...next[pickerState.dayIndex] };
      const p = { ...d.periods[pickerState.periodIndex] };
      if (pickerState.breakIndex >= 0) {
        const br = { ...p.breakTimes[pickerState.breakIndex] };
        br[pickerState.field] = t24;
        p.breakTimes = p.breakTimes.map((b, i) => i === pickerState.breakIndex ? br : b);
      } else {
        p[pickerState.field] = t24;
      }
      d.periods = d.periods.map((pp, i) => i === pickerState.periodIndex ? p : pp);
      next[pickerState.dayIndex] = d;
      return next;
    });
    setPickerVisible(false);
  };

  const updateDay = (dayIndex, newValues) => {
    setDays(current => current.map((day, i) => i === dayIndex ? { ...day, ...newValues } : day));
  };

  const addPeriod = (dayIndex) => {
    setDays(current => {
      const next = [...current];
      const day = { ...next[dayIndex] };
      day.periods = [...day.periods, { openingTime: '09:00', closingTime: '22:00', breakTimes: [] }];
      next[dayIndex] = day;
      return next;
    });
  };

  const removePeriod = (dayIndex, periodIndex) => {
    setDays(current => {
      const next = [...current];
      const day = { ...next[dayIndex] };
      day.periods = day.periods.filter((_, i) => i !== periodIndex);
      next[dayIndex] = day;
      return next;
    });
  };

  const addBreak = (dayIndex, periodIndex) => {
    setDays(current => {
      const next = [...current];
      const day = { ...next[dayIndex] };
      const period = { ...day.periods[periodIndex] };
      period.breakTimes = [...period.breakTimes, { startTime: '12:00', endTime: '13:00' }];
      day.periods[periodIndex] = period;
      next[dayIndex] = day;
      return next;
    });
  };

  const removeBreak = (dayIndex, periodIndex, breakIndex) => {
    setDays(current => {
      const next = [...current];
      const day = { ...next[dayIndex] };
      const period = { ...day.periods[periodIndex] };
      period.breakTimes = period.breakTimes.filter((_, i) => i !== breakIndex);
      day.periods[periodIndex] = period;
      next[dayIndex] = day;
      return next;
    });
  };

  const saveHours = async () => {
    if (!branchId) return;
    setSaving(true);
    try {
      const payload = days.map(d => ({
        dayOfWeek: d.dayOfWeek,
        isOpen: d.isOpen,
        periods: d.isOpen ? d.periods.map(p => ({
          openingTime: p.openingTime,
          closingTime: p.closingTime,
          breakTimes: p.breakTimes.map(b => ({ startTime: b.startTime, endTime: b.endTime }))
        })) : []
      }));
      await updateOperationalHours(branchId, payload);
      if (onSaved) onSaved();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={20} sx={{ color: '#851E41' }} />
        <Typography sx={{ color: '#6B7280', fontSize: 14 }}>Loading hours...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {days.map((day, dayIndex) => (
        <Accordion
          key={day.dayOfWeek}
          expanded={expandedDay === day.dayOfWeek}
          onChange={() => setExpandedDay(expandedDay === day.dayOfWeek ? null : day.dayOfWeek)}
          sx={{
            mb: 1.5,
            borderRadius: '12px !important',
            border: '1px solid #E5E7EB',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#6B7280' }} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mr: 1 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 500, color: '#1F2937' }}>{day.dayOfWeek}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: day.isOpen ? '#10B981' : '#EF4444' }}>
                  {day.isOpen ? 'Open' : 'Closed'}
                </Typography>
                <Switch
                  checked={day.isOpen}
                  onChange={(e) => {
                    e.stopPropagation();
                    updateDay(dayIndex, { isOpen: e.target.checked });
                  }}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#10B981' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10B981' },
                  }}
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ borderTop: '1px solid #E5E7EB', pt: 2 }}>
            {day.isOpen ? (
              <>
                {day.periods.map((period, periodIndex) => (
                  <Box key={periodIndex} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#1F2937' }}>Period {periodIndex + 1}</Typography>
                      {day.periods.length > 1 && (
                        <IconButton size="small" onClick={() => removePeriod(dayIndex, periodIndex)} sx={{ color: '#EF4444' }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<AccessTimeIcon />}
                        onClick={() => showPicker(dayIndex, periodIndex, -1, 'openingTime')}
                        sx={{ flex: 1, justifyContent: 'space-between', borderColor: '#D1D5DB', color: '#1F2937' }}
                      >
                        {formatTimeForDisplay(period.openingTime)}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<AccessTimeIcon />}
                        onClick={() => showPicker(dayIndex, periodIndex, -1, 'closingTime')}
                        sx={{ flex: 1, justifyContent: 'space-between', borderColor: '#D1D5DB', color: '#1F2937' }}
                      >
                        {formatTimeForDisplay(period.closingTime)}
                      </Button>
                    </Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1F2937', mb: 1 }}>Break Times</Typography>
                    {period.breakTimes.map((br, breakIndex) => (
                      <Box key={breakIndex} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => showPicker(dayIndex, periodIndex, breakIndex, 'startTime')}
                          sx={{ flex: 1, justifyContent: 'space-between', borderColor: '#D1D5DB', color: '#1F2937', fontSize: 13 }}
                        >
                          {formatTimeForDisplay(br.startTime)}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => showPicker(dayIndex, periodIndex, breakIndex, 'endTime')}
                          sx={{ flex: 1, justifyContent: 'space-between', borderColor: '#D1D5DB', color: '#1F2937', fontSize: 13 }}
                        >
                          {formatTimeForDisplay(br.endTime)}
                        </Button>
                        <IconButton size="small" onClick={() => removeBreak(dayIndex, periodIndex, breakIndex)} sx={{ color: '#EF4444' }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      size="small"
                      onClick={() => addBreak(dayIndex, periodIndex)}
                      sx={{ color: '#0EA5E9', fontSize: 13, fontWeight: 600, mt: 0.5 }}
                    >
                      Add Break Time
                    </Button>
                  </Box>
                ))}
                <Button
                  startIcon={<AddCircleOutlineIcon />}
                  size="small"
                  onClick={() => addPeriod(dayIndex)}
                  sx={{ color: '#0EA5E9', fontSize: 13, fontWeight: 600 }}
                >
                  Add Period
                </Button>
              </>
            ) : (
              <Typography sx={{ color: '#6B7280', textAlign: 'center', py: 2, fontSize: 14 }}>This day is closed.</Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
      <Button
        fullWidth
        variant="contained"
        onClick={saveHours}
        disabled={saving}
        sx={{
          mt: 1,
          bgcolor: '#0EA5E9',
          '&:hover': { bgcolor: '#0284C7' },
          py: 1.2,
        }}
      >
        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save All Hours'}
      </Button>

      <Dialog open={pickerVisible} onClose={() => setPickerVisible(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: '#1F2937' }}>Select Time</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <TextField
              value={tempHour}
              onChange={(e) => setTempHour(e.target.value.replace(/[^0-9]/g, '').slice(0, 2))}
              placeholder="HH"
              sx={{ width: 70 }}
              inputProps={{ style: { textAlign: 'center' } }}
            />
            <Typography sx={{ color: '#374151', fontWeight: 500 }}>:</Typography>
            <TextField
              value={tempMinute}
              onChange={(e) => setTempMinute(e.target.value.replace(/[^0-9]/g, '').slice(0, 2))}
              placeholder="MM"
              sx={{ width: 70 }}
              inputProps={{ style: { textAlign: 'center' } }}
            />
            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
              <Button
                variant={tempAmPm === 'AM' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setTempAmPm('AM')}
                sx={{
                  minWidth: 48,
                  bgcolor: tempAmPm === 'AM' ? '#851E41' : 'transparent',
                  color: tempAmPm === 'AM' ? '#fff' : '#374151',
                  borderColor: '#D1D5DB',
                }}
              >
                AM
              </Button>
              <Button
                variant={tempAmPm === 'PM' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setTempAmPm('PM')}
                sx={{
                  minWidth: 48,
                  bgcolor: tempAmPm === 'PM' ? '#851E41' : 'transparent',
                  color: tempAmPm === 'PM' ? '#fff' : '#374151',
                  borderColor: '#D1D5DB',
                }}
              >
                PM
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerVisible(false)} sx={{ color: '#1F2937' }}>Cancel</Button>
          <Button onClick={confirmPicker} variant="contained" sx={{ bgcolor: '#851E41', '&:hover': { bgcolor: '#6B1830' } }}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperationalHoursEditor;
